import UriEncoding from '@ms/odsp-utilities/lib/encoding/UriEncoding';

export interface IGuidValue {
  guid: string;
}

export interface IRawValue {
  raw: string;
}

export interface IJsonValue {
  json: string | number | boolean | null | undefined | object;
}

export type IParameterValue = number | boolean | string | IGuidValue | IRawValue | IJsonValue;

/**
 * Represents aliased method arguments passed to a method segment of a SharePoint
 * API URL.
 *
 * @export
 * @interface IMethodArguments
 */
export interface IMethodArguments {
  [name: string]: IParameterValue;
}

/**
 * Represents a builder for SharePoint API URLs.
 *
 * @export
 * @interface IApiUrlBasic
 */
export interface IApiUrlBasic {
  /**
   * Appends a segment to the API URL.
   * Use this to select a sub-object from the current context.
   *
   * @param {string} name
   * @returns {this}
   */
  segment(name: string): this;

  /**
   * Appends a series of segments to the URL.
   *
   * @param {...string[]} segments
   * @returns {this}
   *
   * @memberOf IApiUrlBasic
   */
  segments(...segments: string[]): this;

  /**
   * Appends a segment for a method call, accepting aliased parameter inputs.
   *
   * @param {string} name
   * @param {IMethodArguments} parameters
   * @returns {this}
   */
  methodWithAliases(name: string, parameters: IMethodArguments): this;

  /**
   * Appends a segment for a method call, accepting ordered parameter inputs.
   *
   * @param {string} name
   * @param {...IParameterValue[]} parameters
   * @returns {this}
   */
  method(name: string, ...parameters: IParameterValue[]): this;

  /**
   * Adds a parameter to the end of the URL, encoding the value as appropriate.
   *
   * @param {string} name
   * @param {IParameterValue} value
   * @returns {this}
   */
  parameter(name: string, value: IParameterValue): this;

  /**
   * Adds a set of parameters to the end of the URL, encoding the valus as appropriate.
   *
   * @param {IMethodArguments} arguments
   * @returns {this}
   *
   * @memberOf IApiUrlBasic
   */
  parameters(parameters: IMethodArguments): this;

  /**
   * Appends an ODATA query parameter, assuming the value is already encoded.
   *
   * @param {string} name
   * @param {string} value
   * @returns {this}
   */
  oDataParameter(name: string, value: string): this;

  /**
   * Appends a raw parameter to the end of the URL as an already-encoding query string.
   *
   * @param {string} queryString
   * @returns {this}
   */
  rawParameter(queryString: string): this;

  /**
   * Produces the final URL as a string.
   *
   * @returns {string}
   */
  toString(): string;
}

export interface IApiUrlHelperBasicParams {
  webAbsoluteUrl: string | undefined;
  encodeWebAbsoluteUrl?: boolean;
}

/**
 * Implementation of an extendable SharePoint API URL.
 * Method on this class mutate the underlying object and return the same object back to the caller.
 */
export class ApiUrlBasic implements IApiUrlBasic {
  private _webAbsoluteUrl: string;
  private _encodeWebAbsoluteUrl: boolean;

  private _webUrl: string;
  private _segments: string[];
  private _parameters: IQueryParameter[];
  private _rawParameters: string[];

  private _lastParameterId: number;

  constructor(params: IApiUrlHelperBasicParams) {
    this._webAbsoluteUrl = params.webAbsoluteUrl;
    this._encodeWebAbsoluteUrl = params.encodeWebAbsoluteUrl;

    this._segments = [];
    this._parameters = [];
    this._rawParameters = [];

    this._lastParameterId = 0;
  }

  public toString(): string {
    const querySegments: string[] = [];

    if (this._parameters.length) {
      querySegments.push(this._parameters.map(({
        name,
        serializedValue
      }: IQueryParameter) => `${name}=${serializedValue}`).join('&'));
    }

    querySegments.push(...this._rawParameters);

    let query: string;

    if (querySegments.length) {
      query = `?${querySegments.join('&')}`;
    } else {
      query = '';
    }

    const {
      _webUrl: webUrl = this._webAbsoluteUrl
    } = this;

    const parts = [this._encodeWebAbsoluteUrl ? encodeURI(webUrl) : webUrl, '_api', ...this._segments];

    return `${parts.join('/')}${query}`;
  }

  public segment(name: string): this {
    return this._extend({
      segments: [name]
    });
  }

  public segments(...segments: string[]): this {
    return this._extend({
      segments: segments
    });
  }

  public methodWithAliases(name: string, parameters: IMethodArguments): this {
    let apiUrl = this;

    let methodArguments: string[] = [];

    for (const parameter of Object.keys(parameters)) {
      const value = parameters[parameter];

      const autoParameter = apiUrl._autoParameter(value);

      apiUrl = autoParameter.apiUrl;

      methodArguments.push(`${parameter}=${autoParameter.name}`);
    }

    return apiUrl.segment(`${name}(${methodArguments.join(',')})`);
  }

  public method(name: string, ...parameters: IParameterValue[]): this {
    let apiUrl = this;

    const methodArguments: string[] = [];

    for (const value of parameters) {
      const autoParameter = apiUrl._autoParameter(value);

      apiUrl = autoParameter.apiUrl;

      methodArguments.push(autoParameter.name);
    }

    return apiUrl.segment(`${name}(${methodArguments.join(',')})`);
  }

  public parameter(name: string, value: IParameterValue): this {
    return this._parameter(name, value);
  }

  public parameters(parameters: IMethodArguments): this {
    let apiUrl = this;

    for (const name of Object.keys(parameters)) {
      const value = parameters[name];

      apiUrl = apiUrl.parameter(name, value);
    }

    return apiUrl;
  }

  public oDataParameter(name: string, value: string): this {
    return this.parameter(name, {
      raw: value
    });
  }

  public rawParameter(queryString: string): this {
    return this._extend({
      rawParameters: [queryString]
    });
  }

  private _autoParameter(value: IParameterValue): IAutoParameter<this> {
    const lastParameterId = this._lastParameterId + 1;

    let name = `@a${lastParameterId}`;

    let apiUrl = this._extend({
      lastParameterId: lastParameterId
    })._parameter(name, value);

    return {
      name,
      apiUrl
    };
  }

  private _parameter(name: string, value: IParameterValue): this {
    let serializedValue: string;

    if (isJson(value)) {
      serializedValue = UriEncoding.encodeRestUriStringToken(JSON.stringify(value.json));
    } else if (isGuid(value)) {
      serializedValue = `guid'${value.guid}'`;
    } else if (isRaw(value)) {
      serializedValue = value.raw;
    } else if (typeof value === 'number') {
      serializedValue = `${value}`;
    } else if (typeof value === 'boolean') {
      serializedValue = `${value}`;
    } else {
      serializedValue = `'${UriEncoding.encodeRestUriStringToken(value)}'`;
    }

    return this._extend({
      parameters: [{
        name: name,
        serializedValue: serializedValue
      }]
    });
  }

  protected clone(): this {
    return <this>new ApiUrlBasic({
      webAbsoluteUrl: this._webAbsoluteUrl,
      encodeWebAbsoluteUrl: this._encodeWebAbsoluteUrl
    });
  }

  protected _extend(state: {
    webUrl?: string;
    lastParameterId?: number;
    parameters?: IQueryParameter[];
    rawParameters?: string[];
    segments?: string[];
  } = {}): this {
    const apiUrl = this.clone();

    apiUrl._webUrl = this._webUrl;
    apiUrl._lastParameterId = this._lastParameterId;
    apiUrl._parameters.push(...this._parameters);
    apiUrl._segments.push(...this._segments);
    apiUrl._rawParameters.push(...this._rawParameters);

    if ('webUrl' in state) {
      apiUrl._webUrl = state.webUrl;
    }

    if ('lastParameterId' in state) {
      apiUrl._lastParameterId = state.lastParameterId;
    }

    if ('parameters' in state) {
      apiUrl._parameters.push(...state.parameters);
    }

    if ('rawParameters' in state) {
      apiUrl._rawParameters.push(...state.rawParameters);
    }

    if ('segments' in state) {
      apiUrl._segments.push(...state.segments);
    }

    return <this>apiUrl;
  }
}

/**
 * Component which assists in constructing SharePoint API URLs.
 *
 * @export
 * @class ApiUrlHelperBasic
 *
 * @example
 *  let apiUrlHelper = new ApiUrlHelperBasic({ webAbsoluteUrl: webAbsoluteUrl });
 *  let apiUrl = apiUrlHelper.build()
 *      .segment('web')
 *      .method('GetList', serverRelativeListUrl)
 *      .segment('RenderListFilterData')
 *      .parameter('FieldInternalName', fieldInternalName!);
 *
 *  this._dataRequestor.getData({
 *      url: apiUrl.toString(),
 *      method: 'post',
 *      qosName: 'GetFilterData'
 *  }).then(() => {
 *      // Something.
 *  });
 */
export class ApiUrlHelperBasic {
  protected _apiUrlType: new () => IApiUrlBasic;

  constructor(params: IApiUrlHelperBasicParams) {
    this._apiUrlType = class extends ApiUrlBasic {
      constructor() {
        super({
          webAbsoluteUrl: params.webAbsoluteUrl,
          encodeWebAbsoluteUrl: params.encodeWebAbsoluteUrl
        });
      }
    };
  }

  /**
   * Starts building a new SharePoint API URL.
   *
   * @returns {IApiUrlBasic}
   */
  public build(): IApiUrlBasic {
    return new this._apiUrlType();
  }

  /**
   * Generates a GetContextWebInformation API call URL based on any input URL against
   * a SharePoint server.
   *
   * @param {string} url
   * @returns {string}
   */
  public contextInfoUrl(fullItemUrl: string): string {
    const layoutsIndex = fullItemUrl.indexOf('/_layouts/');

    let contextInfoRootUrl: string;

    if (layoutsIndex > -1) {
      contextInfoRootUrl = fullItemUrl.substring(0, layoutsIndex);
    } else {
      const lastSegmentIndex = fullItemUrl.lastIndexOf('/');
      const lastExtensionIndex = fullItemUrl.lastIndexOf('.');

      if (lastExtensionIndex > lastSegmentIndex) {
        contextInfoRootUrl = `${fullItemUrl.substring(0, lastExtensionIndex)}_${fullItemUrl.substring(lastExtensionIndex + 1)}`;
      } else {
        contextInfoRootUrl = fullItemUrl;
      }
    }

    return `${contextInfoRootUrl}/_api/contextinfo`;
  }
}

interface IAutoParameter<T extends IApiUrlBasic> {
  name: string;
  apiUrl: T;
}

export interface IQueryParameter {
  readonly name: string;
  readonly serializedValue: string;
}

function isGuid(value: IParameterValue): value is IGuidValue {
  return !!value && typeof value === 'object' && ('guid' in <IGuidValue>value);
}

function isRaw(value: IParameterValue): value is IRawValue {
  return !!value && typeof value === 'object' && ('raw' in <IRawValue>value);
}

function isJson(value: IParameterValue): value is IJsonValue {
  return !!value && typeof value === 'object' && ('json' in <IJsonValue>value);
}
